import React, { useEffect, useMemo, useState } from 'react';
import { Box, Flex, Text, Textarea } from '@chakra-ui/react';
import ModalShell from '../../../components/ui/ModalShell';
import { ff } from '../../../theme';
import type { MarketplaceDeclineReasonId } from '../utils/marketplaceDeclineOrderReasons';
import { MARKETPLACE_DECLINE_REASON_OPTIONS } from '../utils/marketplaceDeclineOrderReasons';

const NOTE_MAX = 240;

export interface DeclineMarketplaceOrderSheetProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called after validation; typically invokes decline API then navigates away. */
  onConfirmDecline: (payload: { reasonId: MarketplaceDeclineReasonId; note: string }) => void | Promise<void>;
  isSubmitting?: boolean;
}

export function DeclineMarketplaceOrderSheet({
  isOpen,
  onClose,
  onConfirmDecline,
  isSubmitting,
}: DeclineMarketplaceOrderSheetProps) {
  const defaultReasonId = useMemo(() => MARKETPLACE_DECLINE_REASON_OPTIONS[0]?.id ?? 'niche', []);
  const [selectedReasonId, setSelectedReasonId] = useState<MarketplaceDeclineReasonId>(defaultReasonId);
  const [note, setNote] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setSelectedReasonId(defaultReasonId);
    setNote('');
  }, [isOpen, defaultReasonId]);

  const trimmedNote = note.slice(0, NOTE_MAX);

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      title="Decline this order?"
      cancelLabel="Cancel"
      isCancelDisabled={Boolean(isSubmitting)}
      primary={{
        label: 'Decline order',
        onClick: () => void onConfirmDecline({ reasonId: selectedReasonId, note: trimmedNote }),
        tone: 'danger',
        isLoading: Boolean(isSubmitting),
        loadingText: 'Declining…',
      }}
    >
      <Text fontSize="15px" lineHeight="1.45" color={ff.body} mb={4}>
        Let us know why. This stays private and helps us send you better matches.
      </Text>

      <Box
        border="1px solid"
        borderColor={ff.line}
        borderRadius="20px"
        overflow="hidden"
        role="group"
        aria-label="Decline reasons"
      >
        {MARKETPLACE_DECLINE_REASON_OPTIONS.map((opt, idx) => {
          const selected = selectedReasonId === opt.id;
          const Icon = opt.Icon;
          return (
            <Box
              key={opt.id}
              as="button"
              type="button"
              w="100%"
              textAlign="left"
              borderTopWidth={idx === 0 ? 0 : '1px'}
              borderColor={ff.line}
              bg={selected ? ff.grey : 'white'}
              _hover={{ bg: ff.grey }}
              onClick={() => setSelectedReasonId(opt.id)}
            >
              <Flex align="center" gap={3} px={4} py={3} minH="64px">
                <Flex
                  w="36px"
                  h="36px"
                  borderRadius="12px"
                  align="center"
                  justify="center"
                  flexShrink={0}
                  bg={selected ? ff.ink : ff.grey}
                >
                  <Box as={Icon} boxSize="16px" color={selected ? ff.lime : ff.ink} />
                </Flex>
                <Box flex={1} minW={0}>
                  <Text fontSize="15px" fontWeight="600" color={ff.ink} lineHeight="1.3">
                    {opt.title}
                  </Text>
                  <Text fontSize="13px" color={ff.text2} lineHeight="1.35" mt={0.5}>
                    {opt.description}
                  </Text>
                </Box>
                <Flex
                  w="20px"
                  h="20px"
                  flexShrink={0}
                  borderRadius="full"
                  border="2px solid"
                  borderColor={ff.ink}
                  bg={selected ? ff.ink : 'white'}
                  align="center"
                  justify="center"
                >
                  {selected ? <Box w="8px" h="8px" borderRadius="full" bg={ff.lime} /> : null}
                </Flex>
              </Flex>
            </Box>
          );
        })}
      </Box>

      <Box mt={5}>
        <Flex align="baseline" gap={2} mb={2}>
          <Text textStyle="eyebrow" color={ff.text2}>
            Add a note
          </Text>
          <Text fontSize="12px" color={ff.muted}>
            (optional)
          </Text>
        </Flex>
        <Textarea
          value={trimmedNote}
          onChange={(e) => setNote(e.target.value.slice(0, NOTE_MAX))}
          placeholder="Anything you'd like to share"
          fontSize="16px"
          minH="72px"
          maxH="120px"
          resize="none"
          mb={1}
          isDisabled={Boolean(isSubmitting)}
        />
        <Text fontSize="12px" color={ff.muted} textAlign="right">
          {trimmedNote.length}/{NOTE_MAX}
        </Text>
      </Box>
    </ModalShell>
  );
}
